<script setup lang="ts">
/**
 * Modal — the single canonical popup frame for the whole app.
 *
 * Visual chrome is the "new" consolidated style: dark backdrop, rounded
 * card, slim header (optional back chevron + title + close), optional
 * footer. This replaces the family of hand-rolled `.modal-overlay`/
 * `.modal-card` shells that used to be copy-pasted into each modal —
 * those now mount their inner content into this component instead.
 *
 * Props:
 *   • title         — header text (omit when using the `header` slot)
 *   • size          — sm | md | lg | xl  (md ≈ 560px is the picker default)
 *   • showBack      — render a back chevron that emits `back` (multi-step)
 *   • closeDisabled — block X / backdrop / Esc while a commit is in flight
 *   • stack         — lay the body out as flex-column + gap (the tile/picker
 *                     family). Default is a plain padded block so existing
 *                     form/table modals keep their own internal layout.
 *   • borderless    — drop the header border (legacy flag, kept for compat)
 *
 * Slots: default (body), `header` (replaces title), `footer`.
 * Emits: close, back.
 */
import { computed, watch, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { PhX, PhArrowLeft } from '@phosphor-icons/vue'

export interface ModalProps {
  open: boolean
  title?: string
  size?: 'sm' | 'md' | 'lg' | 'xl'
  borderless?: boolean
  showBack?: boolean
  closeDisabled?: boolean
  stack?: boolean
  /** Editors can place a modal above their canvas and toolbar controls. */
  zIndex?: number
}

const props = withDefaults(defineProps<ModalProps>(), {
  open: false,
  title: '',
  size: 'md',
  borderless: false,
  showBack: false,
  closeDisabled: false,
  stack: false,
})

const emit = defineEmits<{
  close: []
  back: []
}>()

const { t } = useI18n()

const modalClasses = computed(() => [
  'modal-card',
  `modal-${props.size}`
])

function handleClose() {
  if (props.closeDisabled) return
  emit('close')
}

function handleBackdropClick(event: MouseEvent) {
  if (event.target === event.currentTarget) {
    handleClose()
  }
}

function handleEscape(event: KeyboardEvent) {
  if (event.key === 'Escape' && props.open) {
    handleClose()
  }
}

// Handle body scroll lock
watch(() => props.open, (isOpen) => {
  if (isOpen) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
})

onMounted(() => {
  document.addEventListener('keydown', handleEscape)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscape)
  document.body.style.overflow = ''
})
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="open"
        class="modal-overlay"
        :style="zIndex === undefined ? undefined : { zIndex }"
        @click="handleBackdropClick"
      >
        <div :class="modalClasses">
          <!-- Modal Header -->
          <div class="modal-header" :class="{ 'modal-header-borderless': borderless }">
            <button
              v-if="showBack"
              class="modal-back"
              :aria-label="t('common.back')"
              @click="emit('back')"
            >
              <PhArrowLeft :size="16" weight="regular" />
            </button>
            <div v-if="$slots.header" class="modal-header-content">
              <slot name="header" />
            </div>
            <h2 v-else-if="title" class="modal-title">{{ title }}</h2>
            <button
              class="modal-close"
              :disabled="closeDisabled"
              @click="handleClose"
              :aria-label="t('ui.modal.close')"
            >
              <PhX :size="16" weight="regular" />
            </button>
          </div>

          <!-- Modal Content -->
          <div class="modal-content" :class="{ 'modal-content--stack': stack }">
            <slot />
          </div>

          <!-- Modal Footer -->
          <div v-if="$slots.footer" class="modal-footer">
            <slot name="footer" />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-modal, 1000);
  padding: 16px;
}

.modal-card {
  background: var(--color-white);
  border-radius: var(--radius-xl);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.2);
  width: min(560px, calc(100vw - 32px));
  max-height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Size variants — nudged from the legacy scale so `md` lands on the
   ~560px the picker family was hand-coded to. */
.modal-sm { width: min(400px, calc(100vw - 32px)); }
.modal-md { width: min(560px, calc(100vw - 32px)); }
.modal-lg { width: min(720px, calc(100vw - 32px)); }
.modal-xl { width: min(1040px, calc(100vw - 32px)); }

/* Header */
.modal-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--color-gray-100);
  flex-shrink: 0;
}

.modal-header-borderless {
  border-bottom: none;
  padding-bottom: 0;
}

.modal-back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  border: 0;
  border-radius: var(--radius-sm);
  color: var(--color-text-secondary);
  cursor: pointer;
}
.modal-back:hover {
  background: var(--color-background-secondary);
  color: var(--color-primary);
}

.modal-header-content {
  flex: 1;
  min-width: 0;
}

.modal-title {
  flex: 1;
  margin: 0;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-primary);
}

.modal-close {
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
  color: var(--color-text-secondary);
  padding: 4px;
  margin-left: auto;
  transition: all var(--transition-fast);
}
.modal-close:hover:not(:disabled) {
  background: var(--color-background-secondary);
  color: var(--color-primary);
}
.modal-close:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

/* Content */
.modal-content {
  padding: 16px 20px;
  overflow-y: auto;
  flex: 1;
}
.modal-content--stack {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* Footer */
.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 20px;
  border-top: 1px solid var(--color-gray-100);
  flex-shrink: 0;
}

/* Transitions */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}
.modal-enter-active .modal-card,
.modal-leave-active .modal-card {
  transition: transform 0.2s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
.modal-enter-from .modal-card,
.modal-leave-to .modal-card {
  transform: scale(0.97);
}
</style>
